<script lang="ts" setup>
import MIcon from "../MIcon";
import { computed, inject, onUpdated, ref, watch, type ComponentPublicInstance } from "vue";
import { NO_OP, useEvent } from "vue-composable";
import type { MenuType } from "../../../layouts/MedicusLayout/SidebarMenu";
import MedicusLogo from "../../../svg/MedicusLogo.vue";
import AppSearch from "../../misc/AppSearch";
import HubPatientFinder from "../../misc/HubPatientFinder/HubPatientFinder.vue";
import Chat from "../../misc/Chat";
import { usePanicButton } from "../../../store/panicButton";
import MLayoutStack from "../MLayoutStack";
import SidebarGroup from "./SidebarGroup";
import SidebarUser from "./SidebarUser";
import { useDebouncedThrottledFunction } from "../../../composables/throttleDebounce";
import { useTenant, useApp, useAuth } from "../../../store";
import { storeToRefs } from "pinia";

const openChangeWorkspace = inject<() => void>("openChangeWorkspace", NO_OP);

const props = withDefaults(
  defineProps<{
    items?: Array<any>;
    groups?: Array<any>;
    scrollText?: string;
    maxShowItems?: number;
    title: string;
  }>(),
  {
    scrollText: "More items",
  },
);

defineEmits({
  open: (_menu: MenuType) => true,
});

const appStore = useApp();
const { sidebarExpanded } = storeToRefs(appStore);

const auth = useAuth();
// ENG-21322: falls back to the action label if no tenant is loaded yet
const currentWorkspaceName = computed(() => auth.currentTenant?.name ?? "Change Workspace");
const panicButtonStore = usePanicButton();

const canScrollTop = ref(false);
const canScrollBottom = ref(false);
const opened = ref(false);

const refPanicButton = ref<ComponentPublicInstance | null>(null); // ENG-20661 m-button instance (was QBtn)

const supportHref = computed(() => {
  if (useTenant().isSafeguardingService) {
    return "https://medicus-safeguarding.zendesk.com/hc/en-gb"; // Safeguarding Support URL
  } else {
    return "https://medicus-health.zendesk.com/hc/en-gb"; // Default Support URL
  }
});

const visibleGroups = computed(() => {
  if (props.groups) return props.groups;

  const groupObject = (props.items ?? []).reduce((p, c) => {
    const group = c.group || c.title || "";
    if (group) {
      if (p[group]) {
        p[group].items.push(c);
      } else {
        p[group] = {
          maxShowItems: props.maxShowItems,
          items: [],
          ...c,
        };
      }
    } else if (!group) {
      p[c.label] = {
        ...c,
        items: [],
        title: c.label,
      };
      return p;
    }

    return p;
  }, {});

  return Object.values(groupObject) as any[];
});
const hasGrouping = computed(
  () => !!visibleGroups.value.find((x) => x.title && x.items && x.items.length),
);

const contentEl = ref<HTMLDivElement>();

function checkScroll(scroller?: HTMLElement) {
  if (!scroller) return;

  const isTooSmall = scroller.clientHeight <= 200;
  if (isTooSmall) {
    canScrollBottom.value = canScrollTop.value = false;
    return;
  }
  canScrollBottom.value =
    scroller.scrollTop === 0 && scroller.scrollHeight === scroller.clientHeight
      ? false
      : scroller.scrollHeight > scroller.clientHeight + scroller.scrollTop + 10;

  canScrollTop.value =
    scroller.scrollTop < 20 && scroller.scrollHeight === scroller.clientHeight
      ? false
      : scroller.scrollTop > 20;
}

useEvent(contentEl, "scroll", (e) => checkScroll(e.target as HTMLElement), {
  passive: true,
});
useEvent(window, "resize", () => contentEl.value && checkScroll(contentEl.value), {
  passive: true,
});

watch(contentEl, checkScroll);

onUpdated(() => checkScroll(contentEl.value));

function scroll(top: boolean) {
  contentEl.value?.scrollBy({
    top: 200 * (top ? -1 : 1),
    behavior: "smooth",
  });
}

const openMenu = inject<(menu: MenuType) => void>("openMenu", NO_OP);
const isOnline = inject<boolean>("isOnline", true);

function onWheel(e: WheelEvent) {
  contentEl.value?.scrollBy({
    top: e.deltaY,
    behavior: "smooth",
  });
}

const {
  debouncedThrottled: sendPanicAlertDebouncedThrottled,
  cancelDebouncedThrottled: _cancelSendPanicAlertDebouncedThrottled,
} = useDebouncedThrottledFunction(panicButtonStore.sendPanicAlert, 800, 800, true);
</script>
<template>
  <teleport to="#sidenav">
    <nav :class="['m-sidenav', { opened }]" role="navigation" aria-label="Sidebar">
      <div class="sidenav-logo">
        <medicus-logo white />
      </div>
      <section class="top-nav" aria-label="Site navigation">
        <m-layout-stack gap="0" class="overflow-auto">
          <m-button
            :aria-label="!sidebarExpanded ? 'Navigate to Homepage' : undefined"
            :to="{ name: 'homepage' }"
            flat
            dense
          >
            <m-layout-stack horizontal center full>
              <m-icon name="fa-solid fa-house" />
              <span>Homepage</span>
            </m-layout-stack>
          </m-button>

          <AppSearch :opened="sidebarExpanded" />
          <HubPatientFinder v-if="$isHub" />

          <Chat :opened="sidebarExpanded" />

          <m-button
            flat
            dense
            :aria-label="!sidebarExpanded ? 'Go to Modules' : undefined"
            @click="openMenu('menu')"
          >
            <m-layout-stack horizontal center full>
              <m-icon name="fa-solid fa-grid" />
              <span>Modules</span>
            </m-layout-stack>
          </m-button>
        </m-layout-stack>
      </section>

      <div class="sidenav-content-wrapper">
        <section ref="contentEl" class="sidenav-content" aria-label="Module navigation">
          <div class="sidenav-title">
            <slot name="title">
              <h2 v-if="title" class="sidenav-title--title">{{ title }}</h2>
              <div v-else>
                <!-- SPACING  -->
              </div>
            </slot>
          </div>
          <div v-if="$slots.prepend" class="subnav-prepend">
            <slot name="prepend" />
          </div>
          <sidebar-group
            v-for="g in visibleGroups"
            :key="g.title"
            v-bind="g"
            :small="!!(g.items && g.items.length) && hasGrouping"
          />
        </section>

        <div
          v-if="canScrollTop"
          class="scroll-container top cursor-pointer q-mini-drawer-hide"
          aria-hidden="true"
          @click="scroll(true)"
          @wheel="onWheel"
        >
          <p>
            <m-icon name="fa-solid fa-arrow-up" class="q-mr-sm" /> <span>{{ scrollText }}</span>
          </p>
        </div>
        <div
          v-if="canScrollBottom"
          class="scroll-container bottom cursor-pointer q-mini-drawer-hide"
          aria-hidden="true"
          @click="scroll(false)"
          @wheel="onWheel"
        >
          <p>
            <m-icon name="fa-solid fa-arrow-down" class="q-mr-sm" /> <span>{{ scrollText }}</span>
          </p>
        </div>
      </div>

      <section class="bottom-nav" aria-label="User, Support and Panic button">
        <div class="sidenav-support">
          <m-button
            :aria-label="!sidebarExpanded ? 'Go to Support' : undefined"
            title="Support"
            :href="supportHref"
            target="_bank"
            flat
            dense
          >
            <m-layout-stack horizontal full>
              <m-icon name="fa-regular fa-circle-question" />
              <span>Support</span>
            </m-layout-stack>
          </m-button>
        </div>
        <div class="sidenav-change-workspace">
          <m-button
            :aria-label="`Change workspace, current: ${currentWorkspaceName}`"
            title="Change Workspace"
            flat
            dense
            @click="openChangeWorkspace"
          >
            <m-layout-stack horizontal full>
              <m-icon name="fa-solid fa-hospital" />
              <div v-if="sidebarExpanded" class="switcher-info">
                <div class="switcher-name" :title="currentWorkspaceName">
                  {{ currentWorkspaceName }}
                </div>
                <div class="switcher-action">Change</div>
              </div>
            </m-layout-stack>
          </m-button>
        </div>
        <div class="sidenav-user">
          <sidebar-user @click="openMenu('user')" />
        </div>
        <div class="sidenav-panic-button">
          <m-button
            ref="refPanicButton"
            flat
            dense
            :aria-label="!sidebarExpanded ? 'Activate Panic Button' : undefined"
            :disable="
              !!panicButtonStore.serverData?.panicButtonDisabledReason || isOnline === false
            "
            @click="sendPanicAlertDebouncedThrottled"
          >
            <m-layout-stack horizontal full center>
              <div
                class="panic-button"
                :class="{
                  activated: panicButtonStore.isPanicButtonFlashing,
                  'has-responders': panicButtonStore.isSomeoneResponding,
                }"
              />
              <span>Panic Button</span>
            </m-layout-stack>
          </m-button>
          <m-tooltip
            v-if="panicButtonStore.serverData?.panicButtonDisabledReason || isOnline === false"
            :text="
              panicButtonStore.serverData?.panicButtonDisabledReason ||
              'Panic button disabled because you are offline.'
            "
            :scroll-target="refPanicButton?.$el"
          />
        </div>
      </section>
    </nav>
  </teleport>
</template>
<style lang="scss">
// .q-drawer--mini > .sidenav {
//   padding: 0;
// }
.m-sidenav {
  color: var(--grey-lightest);

  background: var(--theme-darkest-blue);

  height: 100%;
  max-height: max(100vh, 100%);

  width: 100%;
  // transition: width 0.2s;

  // width: 0;

  display: flex;
  flex-direction: column;
  overflow: hidden;

  .sidenav-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 60px;
    flex: 0 0 auto;
    margin: 0 15px;

    > svg {
      height: 22px;
    }
  }
  .sidenav-title {
    margin: 0 10px 8px 0;
    display: flex;
    align-items: center;

    .sidenav-title--title {
      color: var(--grey-darker);

      font-family: Arial, sans-serif;
      font-style: normal;
      font-weight: 700;
      font-size: 14px;
      line-height: 21px;
    }
  }

  .top-nav {
    margin: 0px 10px 10px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--theme-darker-blue);
  }

  // ENG-21322: positioning context for the scroll-hint pills, which anchor
  // to this box's own edges (bottom-nav's height is now variable).
  .sidenav-content-wrapper {
    position: relative;
    flex: 1 1 auto;
    // ENG-21322: no overflow: hidden — the pills straddle this box's edges on purpose.
    min-height: 0;
  }

  .sidenav-content {
    height: 100%;
    overflow: auto;

    padding: 0 10px;

    .subnav-prepend {
      margin-bottom: 0.5rem;
      .m-input {
        border-radius: 4px;
        color: var(--grey-darker);
        .q-field__control::before {
          background-color: var(--theme-grey);
        }
        .q-icon,
        .m-icon {
          color: var(--grey-darker);
        }

        input {
          color: var(--grey-lightest) !important;
        }
      }
    }
  }

  .bottom-nav {
    justify-self: flex-end;
    border-top: 2px solid var(--theme-darker-blue);
    padding-top: 10px;
    margin: 0px 10px 10px;
  }

  .sidenav-support {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sidenav-panic-button {
    border-top: 2px solid var(--theme-darker-blue);
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 10px;
    margin-top: 30px;
  }

  .sidenav-user {
    display: flex;
    align-items: center;
  }

  .sidenav-change-workspace {
    display: flex;
    align-items: center;
    justify-content: center;

    .switcher-info {
      display: flex;
      flex-direction: row;
      // ENG-21322: flex-start (not center) so a wrapped name doesn't push Change down.
      align-items: flex-start;
      gap: var(--gap-2);
      flex: 1 1 auto;
      min-width: 0;

      .switcher-name {
        flex: 1 1 auto;
        min-width: 0;
        text-align: left;
        white-space: normal;
        overflow-wrap: break-word;
        font-weight: bold;

        // ENG-21322: wrap up to 2 lines, then ellipsis — caps how much an
        // unusually long workspace name can push the rest of the sidebar down.
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }

      .switcher-action {
        flex: 0 0 auto;
        font-weight: normal;
        text-decoration: underline;
      }
    }
  }
  .sidenav--content {
    padding: 0 15px;
    min-width: 230px;
  }

  .group-with-title:not(:first-child) {
    padding-top: 0;
  }

  .sidenav-change-workspace {
    // ENG-21322: the workspace name can wrap onto a second line, so this
    // button (unlike the other bottom-nav rows) isn't pinned to a fixed 35px.
    .medicus-button-container {
      height: auto;
      min-height: 35px;

      .medicus-button {
        height: auto;
        min-height: 100%;
        padding-top: 8px;
        padding-bottom: 8px;
      }
    }
  }

  .top-nav,
  .sidenav-support,
  .sidenav-panic-button,
  .sidenav-change-workspace {
    // ENG-20661 q-btn → m-button: the rendered root is .medicus-button-container
    .medicus-button-container {
      font-style: normal;
      font-weight: bold;
      font-size: 14px;
      line-height: 1.2;
      border-radius: 4px;

      flex: 1 1 auto;
      // ENG-21322: without min-width: 0 a flex item won't shrink below its
      // content's natural width, so long workspace names get hard-clipped
      // instead of wrapping onto a second line.
      min-width: 0;

      display: flex;

      .medicus-button,
      .m-layout-stack {
        min-width: 0;
      }

      .m-icon,
      i {
        font-size: 18px !important;
      }

      // ENG-21111/ENG-21133: the focusable element is .medicus-button-container (<a> or <button>).
      // Suppress its browser default outline and the ::before ring on the inner span;
      // background fill on the container is the visible indicator.
      &:focus,
      &:focus-visible,
      &:focus-within {
        outline: none !important;
        background: rgba(244, 245, 247, 0.22);
      }
      .medicus-button:focus::before,
      .medicus-button:focus-visible::before {
        display: none;
      }
      .medicus-button:focus,
      .medicus-button:focus-visible {
        outline: none !important;
      }

      .panic-button {
        width: 24px;
        aspect-ratio: 1 / 1;
        background: var(--panic-button-ready);
        border-radius: 50%;
        border: 2px solid var(--grey-lightest);

        &.activated {
          animation: blink 500ms step-end infinite;
        }

        &.has-responders {
          animation-duration: 2000ms;
        }

        @keyframes blink {
          50% {
            background-color: var(--panic-button-activated);
          }
        }
      }

      &.disable .panic-button {
        background: none;
      }
    }
  }

  .top-nav {
    .medicus-button-container .medicus-button {
      padding: 8px;
      // ENG-20661 match q-btn content height (m-button line-height 21px made rows 1px too tall)
      line-height: 18px;
    }
  }

  .bottom-nav {
    // ENG-20661 q-btn grew to content; m-button container caps at 35px + overflow:hidden (clips the
    // user's 2-line row), so let the container size to content like q-btn did.
    .medicus-button-container,
    .medicus-button-container .medicus-button {
      height: auto;
      min-height: 44px;
    }

    .medicus-button-container .medicus-button {
      padding: 8px;
    }

    // ENG-20661 re-centre icon/text (q-btn had .q-btn__content align-items: center)
    .medicus-button-container .m-layout-stack {
      align-items: center;

      // ENG-21322: fixed-width icon slot aligns text across all sidebar buttons
      > .m-icon {
        width: 18px;
        flex-shrink: 0;
        text-align: center;
      }
    }
  }

  .sidenav-support {
    .medicus-button-container .m-icon,
    .medicus-button-container i {
      font-size: 24px !important;
    }
  }
}

.q-drawer--left.q-drawer--bordered {
  border-right: none !important;
}

// ENG-21322: anchored to .sidenav-content-wrapper, not the whole nav — centers
// on the top-nav/bottom-nav boundary line instead of a magic-number offset.
.scroll-container {
  scrollbar-width: thin;

  z-index: 2;

  position: absolute;
  display: flex;
  align-items: center;
  transform: matrix(1, 0, 0, -1, 0, 0);
  margin: 0 !important;
  padding: 0 !important;

  // ENG-21322: straddles the boundary line via a negative offset of half its
  // own height — plain offsets so this composes with the flip transform above.
  left: 50%;
  margin-left: -125px !important; // half of width: 250px
  width: 250px;

  height: 53px;

  p {
    transform: matrix(1, 0, 0, -1, 0, 0);

    margin: auto;
    padding: 3px 13px;

    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 150%;
    /* identical to box height, or 169% */

    display: flex;
    align-items: center;
    letter-spacing: 0.01em;

    color: var(--grey-lightest);
    // /* Primary / Blue Dark / 75 */

    background: var(--theme-grey);
    border-radius: 20px;
  }

  &.bottom {
    // ENG-21322: half of height: 53px centers on .bottom-nav's border-top line.
    bottom: -26.5px;
  }

  &.top {
    // ENG-21322: half of height: 53px + .top-nav's 10px margin-bottom gap.
    top: -36.5px;
  }
}
</style>
